import { hasPermission } from "@avela/avela-authorization-sdk";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Flex,
  Text,
} from "@chakra-ui/react";
import { Formik, FormikProps } from "formik";
import _ from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { BoundariesMap, addressLookup } from "src/components/Boundary/services";
import { useBoundariesMap } from "src/components/Boundary/useBoundary";
import {
  GET_CUSTOM_QUESTION_ANSWER,
  GET_SELECTED_PERSON_ANSWER_BANK,
} from "src/components/Inputs/QuestionDynamicInputs/CustomQuestion/modules/api/graphql/queries";
import {
  FORM_AUTHORIZATION_SCOPE,
  useAuthorizationScoped,
} from "src/components/Providers/AuthorizationProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  FormWithAnswersTracker,
  useFormContext,
  useVisibilityContext,
} from "src/components/Providers/FormProvider";
import { CQT_NO_GUARDIAN_DISABLED_REASON } from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useFormAnswerUpsert } from "src/hooks/useFormAnswerUpsert";
import { useLoadGooglePlacesScript } from "src/hooks/useLoadGoogleMaps";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { SchoolRankInsertInputType } from "src/hooks/useSchoolRank";
import { FormQuestion } from "src/scenes/parent/forms/components/Inputs/FormQuestion";
import { DELETE_ANSWERS } from "src/scenes/parent/forms/graphql/mutations";
import { GET_FORM_ANSWERS_BY_ID } from "src/scenes/parent/forms/graphql/queries";
import { useRemoveAnswersForHiddenQuestions } from "src/scenes/parent/forms/hooks/useRemoveAnswersForHiddenQuestions";
import { useEligibilityService } from "src/services/eligibility/useEligibilityService";
import { Answer } from "src/services/formTemplate";
import {
  findFormAddressAnswer,
  findGradeAnswer,
  getAddressAnswersDeletes,
  getAddressAnswersInserts,
  getCustomQuestionAnswerUpsertPayload,
  getDateAnswersInserts,
  getFreeTextAnswersInserts,
  getGradesAnswersInserts,
  getMultiSelectAnswersInserts,
  getNumberAnswersInserts,
  getSingleSelectAnswersInserts,
} from "src/services/formTemplate/answer";
import {
  RankedSchool,
  calculateSchoolsToRemove,
} from "src/services/formTemplate/preRankingSection";
import * as Question from "src/services/formTemplate/question";
import * as AFF from "src/services/formTemplateFilters";
import { validateFormTemplate } from "src/services/formTemplateValidations";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import {
  GET_FORM_VIEW_BY_ID,
  GET_SCHOOLS_RANK_VIEW,
} from "../../graphql/queries";
import { useUpsertFormSchoolsRank } from "../../hooks/useUpsertFormSchoolsRank";
import { hasInvalidAddressCoordinates } from "./hasInvalidAddressCoordinates";
import { RankedSchoolsRemovalConfirmationDialogBody } from "./RankedSchoolsRemovalConfirmationDialogBody";
import { Undo } from "./Undo";
import { useFrontEndRankedSchoolsRemoval } from "./useFrontEndRankededSchoolsRemoval";

export interface RankedSchoolWithOffersAndWaitlists extends RankedSchool {
  offers: {}[];
  waitlists: {}[];
}

type Props = {
  formTemplateId: uuid;
  formId: uuid;
  guardianId: uuid | undefined;
  applicant: AFF.Types.Applicant;
  schoolRankingSectionId: uuid | undefined;
  schoolRankingSectionKey: string | undefined;
  schoolRankingSectionTitle: string | undefined;
  questions: AF.Question<AF.WithId>[];
  form_answers: GQL.FormAnswerFragment[];
  grades_answers: GQL.GradesAnswersFragment[];
  address_answers: GQL.AddressAnswersFragment[];
  custom_question_answers: GQL.CustomQuestionAnswersFragment[];
  custom_question_answer_bank_relationships?: GQL.CustomQuestionAnswerBankRelationshipsFragment[];
  customQuestionTypes: GQL.GetCustomQuestionTypesByOrg_custom_question_type[];
  rankedSchools: RankedSchoolWithOffersAndWaitlists[];
  onCloseEdit: () => void;
  onRefetchFormAlerts?: () => Promise<void>;
  previousFormSchoolIds: uuid[];
  rankingEnabled: boolean;
};

export const PreRankingSectionEditForm: React.FC<Props> = (props) => {
  const {
    applicant,
    formTemplateId,
    formId,
    guardianId,
    schoolRankingSectionId,
    schoolRankingSectionKey,
    schoolRankingSectionTitle,
    questions,
    form_answers,
    grades_answers,
    address_answers,
    custom_question_answers,
    custom_question_answer_bank_relationships,
    customQuestionTypes,
    rankedSchools,
    onCloseEdit,
    onRefetchFormAlerts,
    previousFormSchoolIds,
  } = props;

  const { updateSchoolsRank } = useUpsertFormSchoolsRank(
    rankedSchools.map((item) => ({
      form_id: formId,
      schools_ranking_section_id: schoolRankingSectionId ?? "",
      school_id: item.school.id,
    }))
  );

  const { upsertFormAnswers, upsertFormAnswersStatus } = useFormAnswerUpsert();
  const [deleteAnswers] = useRemoteDataMutation<
    GQL.DeleteAnswers,
    GQL.DeleteAnswersVariables
  >(DELETE_ANSWERS);

  useLoadGooglePlacesScript();

  const isUpserting = upsertFormAnswersStatus.remoteData.isLoading();

  const initialAnswers = Answer.getFormikInitialValues(
    questions,
    form_answers,
    grades_answers,
    address_answers,
    custom_question_answers,
    custom_question_answer_bank_relationships
  );
  const rankedSchoolIds = rankedSchools.map(
    (rankedSchool) => rankedSchool.school.id
  );
  const toast = useAvelaToast();
  const boundariesMap = useBoundariesMap({ formId });
  const flags = useFlags(["form-service"]);
  const eligibilityService = useEligibilityService({ formTemplateId });
  const visibilityContext = useVisibilityContext();
  const removeAnswersForHiddenQuestions = useRemoveAnswersForHiddenQuestions({
    formId,
  });
  const authorizationMap = useAuthorizationScoped(FORM_AUTHORIZATION_SCOPE);

  const canEditFormSection = React.useCallback(
    (sectionKey: string) => {
      if (authorizationMap.hasData()) {
        return hasPermission({
          action: {
            type: "Form::Action",
            id: "edit",
          },
          resource: {
            type: "FormSection",
            id: sectionKey,
          },
          authorizationMap: authorizationMap.data,
        });
      }
      return false;
    },
    [authorizationMap]
  );

  const calculateSchoolRankingChanges = async (
    schoolRankingSectionId: uuid | undefined,
    completeQuestions: readonly AF.Question<AF.WithId>[],
    answers: Answer.FormikValues,
    rankedSchools: RankedSchool[],
    applicableQuestions: readonly AF.Question<AF.WithId>[],
    boundariesMapData: BoundariesMap
  ): Promise<SchoolRankInsertInputType[]> => {
    if (schoolRankingSectionId === undefined) {
      // no school ranks section, so school ranks should be empty
      return [];
    }

    const gradeConfigId = findGradeAnswer(completeQuestions, answers);
    const address = findFormAddressAnswer(completeQuestions, answers);
    const location =
      address?.geom?.coordinates ??
      (await addressLookup(new google.maps.Geocoder(), address));

    let schoolsIdToRemove: string[];
    if (eligibilityService.enabled) {
      const ineligibleSchoolIds =
        await eligibilityService.getIneligibleSchoolIdsByAnswers({
          dateOfBirth: applicant.birth_date,
          answers,
        });
      schoolsIdToRemove = rankedSchools.flatMap((rs) =>
        ineligibleSchoolIds.includes(rs.school.id) && !rs.is_override
          ? [rs.school.id]
          : []
      );
    } else {
      schoolsIdToRemove = calculateSchoolsToRemove(
        rankedSchools,
        applicableQuestions,
        answers,
        gradeConfigId?.gradeConfigId,
        location,
        boundariesMapData
      ).map((schoolRank) => schoolRank.school.id);
    }

    const newSchoolsRank = _.sortBy(
      rankedSchools.filter(
        (rankedSchool) => !schoolsIdToRemove.includes(rankedSchool.school.id)
      ),
      (schoolRank) => schoolRank.rank
    ).map((schoolRank) => ({
      form_id: formId,
      schools_ranking_section_id: schoolRankingSectionId,
      school_id: schoolRank.school.id,
      is_override: schoolRank.is_override ?? false,
      override_reason: schoolRank.override_reason ?? null,
    }));

    return newSchoolsRank;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitHandler = async (answers: Answer.FormikValues) => {
    try {
      setIsSubmitting(true);
      if (!boundariesMap.hasData()) {
        return;
      }

      if (!window.google?.maps) {
        return;
      }

      const completeQuestions = Question.getCompleteQuestions(questions);
      const applicableQuestions = Question.getCompleteApplicableQuestions(
        questions,
        answers,
        { rankedSchoolIds, previousFormSchoolIds },
        visibilityContext
      );
      const modifiedQuestions = applicableQuestions.filter(
        (q) => !_.isEqual(answers[q.id], initialAnswers[q.id])
      );

      const freeTextAnswers = getFreeTextAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const singleSelectAnswers = getSingleSelectAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const multiSelectAnswers = getMultiSelectAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const singleOrMultiSelectAnswers =
        singleSelectAnswers.concat(multiSelectAnswers);
      const gradesAnswers = getGradesAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const addressAnswers = getAddressAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const addressAnswersDeletes = getAddressAnswersDeletes(
        formId,
        modifiedQuestions,
        answers
      );
      const numberAnswers = getNumberAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const dateAnswers = getDateAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const {
        customQuestionAnswers,
        answerBankMappingIds,
        answerBankAnswers,
        newAnswerBankEntries,
        customQuestionAnswersToDelete,
        customQuestionAnswerFieldsToDelete,
        externalRelationships,
        externalRelationshipsToDelete,
      } = getCustomQuestionAnswerUpsertPayload(
        formId,
        guardianId,
        modifiedQuestions,
        answers,
        customQuestionTypes,
        custom_question_answer_bank_relationships
      );

      const customQuestions = modifiedQuestions.filter(
        (q) => q.type === AF.CustomQuestionType
      );

      const customQuestionIds = customQuestions.map((cq) => cq.id);

      const customQuestionSingleSelectQuestionIds: uuid[] =
        customQuestions.flatMap((q) =>
          (q as AF.CustomQuestion<AF.WithId>).nestedQuestions
            .filter((nq) => nq.type === AF.SingleSelectType)
            .map((nq) => nq.id)
        );
      // Existing single and multi select question answers,
      // including those that are nested in custom questions,
      // will be deleted
      const singleAndMultiSelectQuestionsIds: uuid[] =
        singleOrMultiSelectAnswers
          .map((a) => a.question_id)
          .filter(isNotNull)
          .concat(customQuestionSingleSelectQuestionIds)
          .concat(customQuestionAnswerFieldsToDelete); // this deliberately overloads "singleAndMultiSelectQuestionsIds" to support "Do not answer"

      const newSchoolsRank = await calculateSchoolRankingChanges(
        schoolRankingSectionId,
        completeQuestions,
        answers,
        rankedSchools,
        applicableQuestions,
        boundariesMap.data
      );

      const initialSchoolsRank = _.sortBy(
        rankedSchools,
        (schoolRank) => schoolRank.rank
      ).map((schoolRank) => ({
        form_id: formId,
        schools_ranking_section_id: schoolRankingSectionId,
        school_id: schoolRank.school.id,
        is_override: schoolRank.is_override ?? false,
        override_reason: schoolRank.override_reason ?? null,
      }));
      const ranksChanged = !_.isEqual(newSchoolsRank, initialSchoolsRank);

      if (ranksChanged && schoolRankingSectionKey) {
        if (!authorizationMap.hasData()) {
          toast.error({
            title: "Unable to save",
            description:
              "Permissions are still loading. Please try again in a moment.",
          });
          return;
        }

        if (!canEditFormSection(schoolRankingSectionKey)) {
          const sectionLabel = schoolRankingSectionTitle
            ? `"${schoolRankingSectionTitle}"`
            : "the schools section";
          toast.error({
            title: "Unable to save",
            description: `Your changes would update ${sectionLabel}, but you don't have permission to edit that section.`,
          });
          return;
        }
      }

      const allNewAnswers = freeTextAnswers
        .concat(singleOrMultiSelectAnswers)
        .concat(numberAnswers)
        .concat(dateAnswers);
      const refetchQueries = [
        GET_FORM_VIEW_BY_ID,
        GET_SCHOOLS_RANK_VIEW,
        ...(!flags["form-service"].enabled ? [GET_FORM_ANSWERS_BY_ID] : []),
        // Refetch CQT queries to update outdated answer alerts
        // These queries are used by OutdatedAnswerAlert to compare form answers with answer bank
        GET_CUSTOM_QUESTION_ANSWER,
        GET_SELECTED_PERSON_ANSWER_BANK,
      ];

      const inapplicableQuestions = Question.findInapplicableQuestionsToDelete(
        completeQuestions,
        applicableQuestions,
        answers,
        visibilityContext
      );

      await removeAnswersForHiddenQuestions();
      await Promise.all([
        upsertFormAnswers(
          {
            form_id: formId,
            answers: allNewAnswers,
            custom_question_ids: customQuestionIds,
            single_or_multi_select_question_ids:
              singleAndMultiSelectQuestionsIds,
            grades_answers: gradesAnswers,
            address_answers: addressAnswers,
            address_answer_deletes: addressAnswersDeletes,
            custom_question_answers: customQuestionAnswers,
            answer_bank_ids: answerBankMappingIds.map((ab) => ab.answerBankId),
            answer_bank_answers: answerBankAnswers,
            new_answer_bank_entries: newAnswerBankEntries,
            deleted_answers_custom_question_ids: customQuestionAnswersToDelete,
          },
          {
            answerBankMappingIds,
            externalRelationships,
            externalRelationshipsToDelete,
          },
          {
            refetchQueries,
            awaitRefetchQueries: true,
          }
        ),
        inapplicableQuestions.length > 0
          ? deleteAnswers({
              variables: {
                form_id: formId,
                question_ids: inapplicableQuestions,
              },
            })
          : _.noop(),
        schoolRankingSectionId && ranksChanged
          ? updateSchoolsRank(formId, schoolRankingSectionId, newSchoolsRank)
          : _.noop(),
      ]);

      await onRefetchFormAlerts?.();
      onCloseEdit();
    } catch (error: unknown) {
      console.error(error);
      toast.error({ title: "Error updating form" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik<Answer.FormikValues>
      initialValues={initialAnswers}
      onSubmit={submitHandler}
      validate={validateFormTemplate(
        questions,
        {
          rankedSchoolIds,
          previousFormSchoolIds,
        },
        visibilityContext
      )}
    >
      {(formikProps) => {
        return (
          <EditForm
            {...props}
            boundariesMap={boundariesMap}
            formikProps={formikProps}
            isUpserting={isUpserting}
            rankedSchoolIds={rankedSchoolIds}
            visibilityContext={visibilityContext}
            isSubmitting={isSubmitting}
          />
        );
      }}
    </Formik>
  );
};

type EditFormProps = Props & {
  boundariesMap: RD.RemoteData<Error, BoundariesMap>;
  formikProps: FormikProps<Answer.FormikValues>;
  isUpserting: boolean;
  isSubmitting: boolean;
  rankedSchoolIds: string[];
  visibilityContext: ReturnType<typeof useVisibilityContext>;
};
const EditForm = ({
  formikProps,
  questions,
  rankedSchoolIds,
  previousFormSchoolIds,
  formId,
  formTemplateId,
  guardianId,
  applicant,
  onCloseEdit,
  boundariesMap,
  isUpserting,
  isSubmitting,
  rankedSchools,
  rankingEnabled,
  visibilityContext,
}: EditFormProps) => {
  const completeQuestions = useMemo(
    () =>
      Question.getCompleteApplicableQuestions(
        questions,
        formikProps.values,
        {
          rankedSchoolIds,
          previousFormSchoolIds,
        },
        visibilityContext
      ),
    [
      formikProps.values,
      previousFormSchoolIds,
      questions,
      rankedSchoolIds,
      visibilityContext,
    ]
  );

  const invalidAddressCoordinates = hasInvalidAddressCoordinates(
    completeQuestions,
    formikProps.values
  );

  const authorizationMap = useAuthorizationScoped(FORM_AUTHORIZATION_SCOPE);

  const canEditQuestion = React.useCallback(
    (question: AF.Question<AF.WithId>) => {
      // If there is a CQT in an unlinked form (guardianId is undefined)
      // disable the question as the answer bank functionality requires a guardianId.
      if (question.type === AF.CustomQuestionType && !guardianId) {
        return false;
      }
      if (authorizationMap.hasData()) {
        return hasPermission({
          action: {
            type: "Form::Action",
            id: "edit",
          },
          resource: {
            type: "FormQuestion",
            id: question.key || question.id,
          },
          authorizationMap: authorizationMap.data,
        });
      }
      return false;
    },
    [authorizationMap, guardianId]
  );

  const eligibilityService = useEligibilityService({ formTemplateId });
  const [isEligibilityCheckingState, setIsEligibilityCheckingState] = useState<
    RD.RemoteData<Error, {}>
  >(RD.notAsked());
  const shouldRunEligibilityCheck = useMemo(
    () =>
      rankedSchools.length > 0 &&
      eligibilityService.shouldRunEligibilityCheck({
        questions: completeQuestions,
        answers: {
          previous: formikProps.initialValues,
          current: formikProps.values,
        },
      }),
    [
      eligibilityService,
      formikProps.initialValues,
      formikProps.values,
      completeQuestions,
      rankedSchools.length,
    ]
  );

  const { confirm, confirmationDialog } = useConfirmationDialog({
    body: null,
    header: "Edit eligibility section",
  });
  const flags = useFlags(["grades-answer-auto-revoke-offers"]);
  const isGradesAnswerAutoClearSubstatusEnabled =
    flags["grades-answer-auto-revoke-offers"].enabled;
  const frontEndSchoolsRemoval = useFrontEndRankedSchoolsRemoval({
    formTemplateId,
    formId,
    rankedSchools,
    completeQuestions: completeQuestions,
    answers: formikProps.values,
  });

  const checkSchoolsEligibility = useCallback(async () => {
    setIsEligibilityCheckingState(RD.loading());
    try {
      let schoolsToRemove: RankedSchool[];
      if (!eligibilityService.enabled) {
        if (!frontEndSchoolsRemoval.isReady) {
          console.error(
            "Unable to perform front end eligibility check, data is not fully loaded yet."
          );
          return;
        }
        schoolsToRemove = await frontEndSchoolsRemoval.calculateRemovals();
      } else {
        const ineligibleSchoolIds =
          await eligibilityService.getIneligibleSchoolIdsByAnswers({
            dateOfBirth: applicant.birth_date,
            answers: formikProps.values,
          });
        schoolsToRemove = rankedSchools.filter(
          (rs) => ineligibleSchoolIds.includes(rs.school.id) && !rs.is_override
        );
      }
      setIsEligibilityCheckingState(RD.success({}));

      // If a grades answer changes, offers and waitlists may be cleared.
      const hasGradesAnswerChanged = completeQuestions.some(
        (question) =>
          question.type === AF.GradesType &&
          !_.isEqual(
            formikProps.values[question.id],
            formikProps.initialValues[question.id]
          )
      );
      const schoolsToClearSubstatus =
        isGradesAnswerAutoClearSubstatusEnabled && hasGradesAnswerChanged
          ? _.differenceBy(
              rankedSchools.filter(
                (schoolRank) =>
                  schoolRank.offers.length > 0 ||
                  schoolRank.waitlists.length > 0
              ),
              schoolsToRemove,
              (s) => s.school.id
            )
          : [];

      if (
        schoolsToRemove.length === 0 &&
        schoolsToClearSubstatus.length === 0
      ) {
        await formikProps.submitForm();
        return;
      }

      const isConfirmed = await confirm({
        body: (
          <RankedSchoolsRemovalConfirmationDialogBody
            rankingEnabled={rankingEnabled}
            schoolsToRemove={schoolsToRemove}
            schoolsToClearSubstatus={schoolsToClearSubstatus}
          />
        ),
      });
      if (!isConfirmed) {
        return;
      }

      formikProps.submitForm();
    } catch (error) {
      console.error(error);
      setIsEligibilityCheckingState(RD.failure(error as Error));
    }
  }, [
    confirm,
    eligibilityService,
    formikProps,
    frontEndSchoolsRemoval,
    rankedSchools,
    rankingEnabled,
    applicant,
    isGradesAnswerAutoClearSubstatusEnabled,
    completeQuestions,
  ]);

  const formContext = useFormContext();
  return (
    <Flex direction="column" as={FormWithAnswersTracker} noValidate gap={3}>
      {confirmationDialog}
      <Flex direction="column" gap="2">
        {completeQuestions.map((question) => (
          <FormQuestion
            formId={formId}
            applicant={applicant}
            key={question.id}
            question={question}
            marginBottom={4}
            disableAddressBook
            disableDocumentLibray
            isDisabled={
              isSubmitting || isUpserting || !canEditQuestion(question)
            }
            disabledReason={
              question.type === AF.CustomQuestionType && !guardianId
                ? CQT_NO_GUARDIAN_DISABLED_REASON
                : undefined
            }
            labelPostElement={
              <Undo
                answer={formikProps.values[question.id]}
                question={question}
                formId={formId}
                setAnswer={(value) =>
                  formikProps.setFieldValue(question.id, value)
                }
              />
            }
          />
        ))}
        {shouldRunEligibilityCheck &&
          !isEligibilityCheckingState.hasError() && (
            <Alert status="warning" rounded="md">
              <AlertIcon />
              <AlertDescription>
                {isGradesAnswerAutoClearSubstatusEnabled ? (
                  <>
                    <Text>
                      By changing the responses in this section, you may impact
                      eligibility.
                    </Text>
                    <Text>
                      <Text as="span" fontWeight="bold">
                        This may clear all sub-statuses and certain schools may
                        be removed
                      </Text>{" "}
                      from the selection.
                    </Text>
                  </>
                ) : (
                  <>
                    By changing the response on this question, you may impact
                    eligibility and certain schools may be removed from the
                    selection.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}
        {isEligibilityCheckingState.hasError() && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Unable to check eligibility</AlertTitle>
            <AlertDescription>
              Our engines are not powering up. Try again later.
            </AlertDescription>
          </Alert>
        )}
      </Flex>
      <Flex justifyContent="flex-end" gap={3}>
        <Button
          type="button"
          variant="ghost"
          colorScheme="gray"
          onClick={() => {
            formContext.updateAnswers(formikProps.initialValues);
            onCloseEdit();
          }}
        >
          Cancel
        </Button>
        {shouldRunEligibilityCheck ? (
          <Button
            type="button"
            onClick={checkSchoolsEligibility}
            isDisabled={
              isEligibilityCheckingState.isLoading() ||
              (!eligibilityService.enabled &&
                !frontEndSchoolsRemoval.isReady) ||
              invalidAddressCoordinates
            }
            isLoading={
              isEligibilityCheckingState.isLoading() ||
              isUpserting ||
              isSubmitting
            }
            loadingText={
              isEligibilityCheckingState.isLoading()
                ? "Checking eligibility..."
                : undefined
            }
          >
            Save
          </Button>
        ) : (
          <Button
            type="submit"
            isDisabled={
              !formikProps.isValid ||
              !boundariesMap.hasData() ||
              invalidAddressCoordinates
            }
            isLoading={isUpserting || isSubmitting}
          >
            Save
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
